import { Injectable, inject } from '@angular/core';
import { IDecaDocument, UUID } from '@okcargo-apps/shared-domain';
import {
  AddDigitalControlDocReservationCommand,
  AddDigitalControlDocReservationCommandHandler,
  CreateDigitalControlDocFromSegmentsAsCarrierCommandHandler,
  CreateDigitalControlDocFromSegmentsAsShipperCommandHandler,
  ImportDcdsAsShipperBatchCommand,
  ImportDcdsAsShipperBatchCommandHandler,
  RequestDigitalControlDocCreditsAsCarrierCommand,
  RequestDigitalControlDocCreditsAsCarrierCommandHandler,
  RequestDigitalControlDocCreditsAsShipperCommand,
  RequestDigitalControlDocCreditsAsShipperCommandHandler,
  UpdateDigitalControlDocFromSegmentsAsCarrierCommandHandler,
  UpdateDigitalControlDocFromSegmentsAsShipperCommandHandler,
  UpdateDigitalControlDocPlateNumbersAsCarrierCommand,
  UpdateDigitalControlDocPlateNumbersAsCarrierCommandHandler,
  UpdateDigitalControlDocSegmentDestinationAddressAsShipperCommand,
  UpdateDigitalControlDocSegmentDestinationAddressAsShipperCommandHandler,
  UpdateDigitalControlDocSegmentGoodsDescriptionAsShipperCommand,
  UpdateDigitalControlDocSegmentGoodsDescriptionAsShipperCommandHandler,
  UpdateDigitalControlDocSegmentLoadingDateAsCarrierCommand,
  UpdateDigitalControlDocSegmentLoadingDateAsCarrierCommandHandler,
  UpdateDigitalControlDocSegmentNetWeightAsShipperCommand,
  UpdateDigitalControlDocSegmentNetWeightAsShipperCommandHandler,
  UpdateDigitalControlDocSegmentOriginAddressAsShipperCommand,
  UpdateDigitalControlDocSegmentOriginAddressAsShipperCommandHandler,
  UpdateDigitalControlDocSegmentUnloadingDateAsCarrierCommand,
  UpdateDigitalControlDocSegmentUnloadingDateAsCarrierCommandHandler,
  UpdateDigitalControlDocSpecialAuthorizationAsCarrierCommand,
  UpdateDigitalControlDocSpecialAuthorizationAsCarrierCommandHandler,
} from '@okcargo/command-processor';
import { BaseCommandRepository } from '../command-repository/command.repository';
import {
  transformFromIDecaDocumentToCreateAsCarrierCommand,
  transformFromIDecaDocumentToCreateAsShipperCommand,
  transformFromIDecaDocumentToUpdateAsCarrierCommand,
  transformFromIDecaDocumentToUpdateAsShipperCommand,
} from './deca-command.mapper';

@Injectable({ providedIn: 'root' })
export class DecaCommandService {
  private readonly createDigitalControlDocFromSegmentsAsShipperCommandHandler: CreateDigitalControlDocFromSegmentsAsShipperCommandHandler;
  private readonly updateDigitalControlDocFromSegmentsAsShipperCommandHandler: UpdateDigitalControlDocFromSegmentsAsShipperCommandHandler; // TODO fix interface name, should be UpdateDigitalControlDocFromSegmentsAsShipperCommandHandler;
  private readonly createDigitalControlDocFromSegmentsAsCarrierCommandHandler: CreateDigitalControlDocFromSegmentsAsCarrierCommandHandler;
  private readonly updateDigitalControlDocFromSegmentsAsCarrierCommandHandler: UpdateDigitalControlDocFromSegmentsAsCarrierCommandHandler;
  private readonly addDigitalControlDocReservationCommandHandler: AddDigitalControlDocReservationCommandHandler;
  private readonly updateDigitalControlDocPlateNumbersAsCarrierCommandHandler: UpdateDigitalControlDocPlateNumbersAsCarrierCommandHandler;
  private readonly updateDigitalControlDocUnloadingDateAsCarrierCommandHandler: UpdateDigitalControlDocSegmentUnloadingDateAsCarrierCommandHandler;
  private readonly updateDigitalControlDocLoadingDateAsCarrierCommandHandler: UpdateDigitalControlDocSegmentLoadingDateAsCarrierCommandHandler;
  private readonly updateDigitalControlDocSpecialAuthorizationAsCarrierCommandHandler: UpdateDigitalControlDocSpecialAuthorizationAsCarrierCommandHandler;
  private readonly updateDigitalControlDocGoodsDescriptionAsShipperCommandHandler: UpdateDigitalControlDocSegmentGoodsDescriptionAsShipperCommandHandler;
  private readonly updateDigitalControlDocNetWeightAsShipperCommandHandler: UpdateDigitalControlDocSegmentNetWeightAsShipperCommandHandler;
  private readonly updateDigitalControlDocOriginAddressAsShipperCommandHandler: UpdateDigitalControlDocSegmentOriginAddressAsShipperCommandHandler;
  private readonly updateDigitalControlDocDestinationAddressAsShipperCommandHandler: UpdateDigitalControlDocSegmentDestinationAddressAsShipperCommandHandler;
  private readonly importDcdsAsShipperBatchCommandHandler: ImportDcdsAsShipperBatchCommandHandler;
  private readonly requestDigitalControlDocCreditsAsCarrierCommandHandler: RequestDigitalControlDocCreditsAsCarrierCommandHandler;
  private readonly requestDigitalControlDocCreditsAsShipperCommandHandler: RequestDigitalControlDocCreditsAsShipperCommandHandler;

  constructor() {
    const commandRepository = inject(BaseCommandRepository);

    this.createDigitalControlDocFromSegmentsAsShipperCommandHandler =
      new CreateDigitalControlDocFromSegmentsAsShipperCommandHandler(commandRepository);
    this.updateDigitalControlDocFromSegmentsAsShipperCommandHandler =
      new UpdateDigitalControlDocFromSegmentsAsShipperCommandHandler(commandRepository);
    this.createDigitalControlDocFromSegmentsAsCarrierCommandHandler =
      new CreateDigitalControlDocFromSegmentsAsCarrierCommandHandler(commandRepository);
    this.updateDigitalControlDocFromSegmentsAsCarrierCommandHandler =
      new UpdateDigitalControlDocFromSegmentsAsCarrierCommandHandler(commandRepository);
    this.addDigitalControlDocReservationCommandHandler = new AddDigitalControlDocReservationCommandHandler(
      commandRepository,
    );
    this.updateDigitalControlDocPlateNumbersAsCarrierCommandHandler =
      new UpdateDigitalControlDocPlateNumbersAsCarrierCommandHandler(commandRepository);
    this.updateDigitalControlDocUnloadingDateAsCarrierCommandHandler =
      new UpdateDigitalControlDocSegmentUnloadingDateAsCarrierCommandHandler(commandRepository);
    this.updateDigitalControlDocLoadingDateAsCarrierCommandHandler =
      new UpdateDigitalControlDocSegmentLoadingDateAsCarrierCommandHandler(commandRepository);
    this.updateDigitalControlDocSpecialAuthorizationAsCarrierCommandHandler =
      new UpdateDigitalControlDocSpecialAuthorizationAsCarrierCommandHandler(commandRepository);
    this.updateDigitalControlDocGoodsDescriptionAsShipperCommandHandler =
      new UpdateDigitalControlDocSegmentGoodsDescriptionAsShipperCommandHandler(commandRepository);
    this.updateDigitalControlDocNetWeightAsShipperCommandHandler =
      new UpdateDigitalControlDocSegmentNetWeightAsShipperCommandHandler(commandRepository);
    this.updateDigitalControlDocOriginAddressAsShipperCommandHandler =
      new UpdateDigitalControlDocSegmentOriginAddressAsShipperCommandHandler(commandRepository);
    this.updateDigitalControlDocDestinationAddressAsShipperCommandHandler =
      new UpdateDigitalControlDocSegmentDestinationAddressAsShipperCommandHandler(commandRepository);
    this.importDcdsAsShipperBatchCommandHandler = new ImportDcdsAsShipperBatchCommandHandler(commandRepository);
    this.requestDigitalControlDocCreditsAsCarrierCommandHandler =
      new RequestDigitalControlDocCreditsAsCarrierCommandHandler(commandRepository);
    this.requestDigitalControlDocCreditsAsShipperCommandHandler =
      new RequestDigitalControlDocCreditsAsShipperCommandHandler(commandRepository);
  }

  public async createDocumentAsShipper(document: IDecaDocument): Promise<void> {
    const command = transformFromIDecaDocumentToCreateAsShipperCommand(document);
    return this.createDigitalControlDocFromSegmentsAsShipperCommandHandler.handle(command);
  }

  public async updateDocumentAsShipper(document: IDecaDocument, changeReason = ''): Promise<void> {
    const command = transformFromIDecaDocumentToUpdateAsShipperCommand(document, changeReason);
    return this.updateDigitalControlDocFromSegmentsAsShipperCommandHandler.handle(command);
  }

  public async createDocumentAsCarrier(document: IDecaDocument): Promise<void> {
    const command = transformFromIDecaDocumentToCreateAsCarrierCommand(document);
    return this.createDigitalControlDocFromSegmentsAsCarrierCommandHandler.handle(command);
  }

  public async updateDocumentAsCarrier(document: IDecaDocument, changeReason = ''): Promise<void> {
    const command = transformFromIDecaDocumentToUpdateAsCarrierCommand(document, changeReason);
    return this.updateDigitalControlDocFromSegmentsAsCarrierCommandHandler.handle(command);
  }

  public async addReservation(document: IDecaDocument): Promise<void> {
    const reservation = document.reservations[document.reservations.length - 1];

    if (!reservation) {
      throw new Error('Cannot add an empty reservation to the document.');
    }

    const command: AddDigitalControlDocReservationCommand = {
      id: document.id,
      reservations: reservation,
    };
    return this.addDigitalControlDocReservationCommandHandler.handle(command);
  }

  public async updatePlateNumbers(document: IDecaDocument, changeReason = ''): Promise<void> {
    const command: UpdateDigitalControlDocPlateNumbersAsCarrierCommand = {
      id: document.id,
      tractorPlateNumber: document.tractorPlateNumber ?? '',
      trailerPlateNumber: document.trailerPlateNumber ?? '',
      changeReason,
    };
    return this.updateDigitalControlDocPlateNumbersAsCarrierCommandHandler.handle(command);
  }

  public async updateOriginDate(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    const segment = document.segments.find((item) => item.id === segmentId);

    if (!segment) {
      throw new Error(`Segment not found with id ${segmentId}`);
    }

    const command: UpdateDigitalControlDocSegmentLoadingDateAsCarrierCommand = {
      id: document.id,
      segmentId: segment.id,
      date: segment.origin.date!,
      changeReason,
    };
    return this.updateDigitalControlDocLoadingDateAsCarrierCommandHandler.handle(command);
  }

  public async updateDestinationDate(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    const segment = document.segments.find((item) => item.id === segmentId);

    if (!segment) {
      throw new Error(`Segment not found with id ${segmentId}`);
    }

    const command: UpdateDigitalControlDocSegmentUnloadingDateAsCarrierCommand = {
      id: document.id,
      segmentId: segment.id,
      date: segment.destination.date!, // At this point, always has date data. If not, then backend fails and we need to manage the error response.
      changeReason,
    };
    return this.updateDigitalControlDocUnloadingDateAsCarrierCommandHandler.handle(command);
  }

  public async updateSpecialAuthorization(document: IDecaDocument, changeReason = ''): Promise<void> {
    const command: UpdateDigitalControlDocSpecialAuthorizationAsCarrierCommand = {
      id: document.id,
      specialAuthorization: document.specialAuthorizationType ?? '',
      changeReason,
    };
    return this.updateDigitalControlDocSpecialAuthorizationAsCarrierCommandHandler.handle(command);
  }

  public async updateGoodsDescription(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    const segment = document.segments.find((item) => item.id === segmentId);

    if (!segment) {
      throw new Error(`Segment not found with id ${segmentId}`);
    }

    const command: UpdateDigitalControlDocSegmentGoodsDescriptionAsShipperCommand = {
      id: document.id,
      segmentId: segment.id,
      goodsDescription: segment.goodsDescription,
      changeReason,
    };
    return this.updateDigitalControlDocGoodsDescriptionAsShipperCommandHandler.handle(command);
  }

  public async updateNetWeight(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    const segment = document.segments.find((item) => item.id === segmentId);

    if (!segment) {
      throw new Error(`Segment not found with id ${segmentId}`);
    }

    const command: UpdateDigitalControlDocSegmentNetWeightAsShipperCommand = {
      id: document.id,
      segmentId: segment.id,
      netWeight: segment.netWeight || 0,
      changeReason,
    };
    return this.updateDigitalControlDocNetWeightAsShipperCommandHandler.handle(command);
  }

  public async updateOriginAddress(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    const segment = document.segments.find((item) => item.id === segmentId);

    if (!segment) {
      throw new Error(`Segment not found with id ${segmentId}`);
    }

    const command: UpdateDigitalControlDocSegmentOriginAddressAsShipperCommand = {
      id: document.id,
      segmentId: segment.id,
      companyName: segment.origin.companyName,
      street: segment.origin.street,
      city: segment.origin.city,
      postalCode: segment.origin.postalCode,
      stateCode: segment.origin.stateCode,
      countryCode: segment.origin.countryCode,
      changeReason,
    };
    return this.updateDigitalControlDocOriginAddressAsShipperCommandHandler.handle(command);
  }
  public async updateDestinationAddress(document: IDecaDocument, segmentId: UUID, changeReason = ''): Promise<void> {
    const segment = document.segments.find((item) => item.id === segmentId);

    if (!segment) {
      throw new Error(`Segment not found with id ${segmentId}`);
    }

    const command: UpdateDigitalControlDocSegmentDestinationAddressAsShipperCommand = {
      id: document.id,
      segmentId: segment.id,
      companyName: segment.destination.companyName,
      street: segment.destination.street,
      city: segment.destination.city,
      postalCode: segment.destination.postalCode,
      stateCode: segment.destination.stateCode,
      countryCode: segment.destination.countryCode,
      changeReason,
    };
    return this.updateDigitalControlDocDestinationAddressAsShipperCommandHandler.handle(command);
  }

  public async uploadFileCreateDocumentBatchAsShipper(data: string): Promise<void> {
    const command: ImportDcdsAsShipperBatchCommand = {
      id: crypto.randomUUID(),
      data: data,
    };
    return this.importDcdsAsShipperBatchCommandHandler.handle(command);
  }

  public async requestCreditsAsCarrier(): Promise<void> {
    const command: RequestDigitalControlDocCreditsAsCarrierCommand = {
      id: crypto.randomUUID(),
    };
    return this.requestDigitalControlDocCreditsAsCarrierCommandHandler.handle(command);
  }

  public async requestCreditsAsShipper(): Promise<void> {
    const command: RequestDigitalControlDocCreditsAsShipperCommand = {
      id: crypto.randomUUID(),
    };
    return this.requestDigitalControlDocCreditsAsShipperCommandHandler.handle(command);
  }
}
